import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAvaliacao } from "../context/AvaliacaoRestauranteContext";
import { useRestaurante } from "../context/RestauranteContext";
import { useLoading } from "../context/LoadingContext";
import { FaStar, FaUser, FaCalendarAlt } from "react-icons/fa";
import "../css/ListarAvaliacoes.css";

const ListarAvaliacoes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { buscarAvaliacoesPaginadas, buscarMediaAvaliacoes } = useAvaliacao();
  const { restaurantes } = useRestaurante();
  const { isLoading, setIsLoading } = useLoading();
  
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [mediaAvaliacoes, setMediaAvaliacoes] = useState(null);
  const [restaurante, setRestaurante] = useState(null);
  
  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const itensPorPagina = 5;
  const [listaDisponivel, setListaDisponivel] = useState(false);

  useEffect(() => {
    carregarDados();
  }, [id, paginaAtual]);

  const carregarDados = async () => {
    setIsLoading(true);
    try {
      // Médias são públicas e carregam sempre, independentes da lista (que exige login).
      if (paginaAtual === 0) {
        const medias = await buscarMediaAvaliacoes(id).catch(() => null);
        setMediaAvaliacoes(medias);
      }

      // Lista de comentários: só para quem está logado (null sem login).
      const response = await buscarAvaliacoesPaginadas(id, paginaAtual, itensPorPagina).catch(() => null);
      setListaDisponivel(Boolean(response));
      setAvaliacoes(response?.content || []);
      setTotalPaginas(response?.totalPages || 0);
      setTotalElements(response?.totalElements || 0);

      const rest = restaurantes?.find(r => r.id === Number(id));
      setRestaurante(rest);
    } catch (error) {
      console.error("Erro ao carregar avaliações:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Renderiza estrelas
  const renderStars = (rating, size = 16) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span 
          key={i} 
          className={`star ${i <= fullStars ? 'filled' : ''}`}
          style={{ fontSize: size }}
        >
          ★
        </span>
      );
    }
    return stars;
  };

  // Formata data
  const formatarData = (data) => {
    if (!data) return "";
    const date = new Date(data);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Calcula distribuição de notas
  const calcularDistribuicao = () => {
    const distribuicao = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    
    avaliacoes.forEach(av => {
      const nota = Math.round(av.mediaGeral || 0);
      if (nota >= 1 && nota <= 5) {
        distribuicao[nota]++;
      }
    });
    
    return distribuicao;
  };

  const distribuicao = calcularDistribuicao();

  // Funções de paginação
  const irParaPagina = (pagina) => {
    if (pagina >= 0 && pagina < totalPaginas) {
      setPaginaAtual(pagina);
    }
  };

  const paginaAnterior = () => {
    if (paginaAtual > 0) {
      setPaginaAtual(paginaAtual - 1);
    }
  };

  const proximaPagina = () => {
    if (paginaAtual < totalPaginas - 1) {
      setPaginaAtual(paginaAtual + 1);
    }
  };

  if (isLoading) {
    return (
      <div className="avaliacoes-container">
        <p className="loading-text">Carregando avaliações...</p>
      </div>
    );
  }

  return (
    <div className="avaliacoes-container">
      {/* Header */}
      <div className="avaliacoes-header">
        <h1>Avaliações</h1>
        <p className="restaurante-nome">{restaurante?.nome || mediaAvaliacoes?.restauranteNome || "Restaurante"}</p>
      </div>

      {/* Sem avaliações: em vez de "0.0" e estrelas vazias, um convite para avaliar. */}
      {mediaAvaliacoes && !(mediaAvaliacoes.totalAvaliacoes > 0) ? (
        <div className="sem-avaliacoes-container">
          <FaStar className="sem-avaliacoes-icon" />
          <p>Sem avaliações ainda.</p>
          <button type="button" className="btn-avaliar-primeiro" onClick={() => navigate(`/restaurantes/${id}/avaliar`)}>
            Seja o primeiro a avaliar
          </button>
        </div>
      ) : (<>
      {/* Card de Visão Geral */}
      <div className="visao-geral-card">
        <div className="visao-geral-esquerda">
          <span className="nota-grande">
            {mediaAvaliacoes?.mediaGeral?.toFixed(1) || '0.0'}
          </span>
          <div className="estrelas-geral">
            {renderStars(mediaAvaliacoes?.mediaGeral || 0, 24)}
          </div>
          <span className="total-avaliacoes">
            {mediaAvaliacoes?.totalAvaliacoes || 0} {(mediaAvaliacoes?.totalAvaliacoes || 0) === 1 ? 'avaliação' : 'avaliações'}
          </span>
        </div>

        {/* A distribuição é calculada a partir dos comentários, que só aparecem para quem está logado. */}
        {listaDisponivel && (
        <div className="visao-geral-direita">
          {/* Barras de distribuição */}
          {[5, 4, 3, 2, 1].map((nota) => (
            <div key={nota} className="distribuicao-linha">
              <span className="distribuicao-nota">{nota}</span>
              <div className="distribuicao-barra">
                <div 
                  className="distribuicao-barra-fill"
                  style={{ 
                    width: (mediaAvaliacoes?.totalAvaliacoes || 0) > 0 
                      ? `${(distribuicao[nota] / (mediaAvaliacoes?.totalAvaliacoes || 1)) * 100}%` 
                      : '0%' 
                  }}
                ></div>
              </div>
              <span className="distribuicao-count">{distribuicao[nota]}</span>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* Cards de Categorias */}
      <div className="categorias-resumo">
        <div className="categoria-resumo-item">
          <span className="categoria-emoji">🏠</span>
          <div className="categoria-resumo-info">
            <span className="categoria-resumo-nome">Ambiente</span>
            <span className="categoria-resumo-nota">
              {mediaAvaliacoes?.mediaAmbiente?.toFixed(1) || '0.0'}
            </span>
          </div>
          <div className="categoria-resumo-stars">
            {renderStars(mediaAvaliacoes?.mediaAmbiente || 0, 14)}
          </div>
        </div>

        <div className="categoria-resumo-item">
          <span className="categoria-emoji">🍽️</span>
          <div className="categoria-resumo-info">
            <span className="categoria-resumo-nome">Comida</span>
            <span className="categoria-resumo-nota">
              {mediaAvaliacoes?.mediaComida?.toFixed(1) || '0.0'}
            </span>
          </div>
          <div className="categoria-resumo-stars">
            {renderStars(mediaAvaliacoes?.mediaComida || 0, 14)}
          </div>
        </div>

        <div className="categoria-resumo-item">
          <span className="categoria-emoji">👨‍🍳</span>
          <div className="categoria-resumo-info">
            <span className="categoria-resumo-nome">Atendimento</span>
            <span className="categoria-resumo-nota">
              {mediaAvaliacoes?.mediaAtendimento?.toFixed(1) || '0.0'}
            </span>
          </div>
          <div className="categoria-resumo-stars">
            {renderStars(mediaAvaliacoes?.mediaAtendimento || 0, 14)}
          </div>
        </div>

        <div className="categoria-resumo-item">
          <span className="categoria-emoji">💰</span>
          <div className="categoria-resumo-info">
            <span className="categoria-resumo-nome">Custo-Benefício</span>
            <span className="categoria-resumo-nota">
              {mediaAvaliacoes?.mediaPreco?.toFixed(1) || '0.0'}
            </span>
          </div>
          <div className="categoria-resumo-stars">
            {renderStars(mediaAvaliacoes?.mediaPreco || 0, 14)}
          </div>
        </div>
      </div>

      </>)}

      {/* Lista de Avaliações (privada: só para quem está logado) */}
      {listaDisponivel && (<>
      <div className="avaliacoes-lista-header">
        <h2>Últimas avaliações</h2>
      </div>

      {avaliacoes.length === 0 ? (
        <div className="sem-avaliacoes-container">
          <FaStar className="sem-avaliacoes-icon" />
          <p>Nenhuma avaliação ainda.</p>
        </div>
      ) : (
        <div className="avaliacoes-lista">
          {avaliacoes.map((avaliacao) => (
            <div key={avaliacao.id} className="avaliacao-card">
              {/* Header do card */}
              <div className="avaliacao-card-header">
                <div className="avaliacao-usuario">
                  <div className="avaliacao-avatar">
                    <FaUser />
                  </div>
                  <div className="avaliacao-usuario-info">
                    <span className="avaliacao-nome">
                      {avaliacao.nomeCliente || "Anônimo"}
                    </span>
                    <span className="avaliacao-data">
                      <FaCalendarAlt /> {formatarData(avaliacao.dataAvaliacao)}
                    </span>
                  </div>
                </div>
                <div className="avaliacao-nota-geral">
                  <span className="nota-valor">{avaliacao.mediaGeral?.toFixed(1)}</span>
                  <div className="nota-estrelas">
                    {renderStars(avaliacao.mediaGeral || 0, 14)}
                  </div>
                </div>
              </div>

              {/* Notas por categoria */}
              <div className="avaliacao-categorias">
                <div className="avaliacao-categoria">
                  <span className="cat-label">Ambiente</span>
                  <div className="cat-stars">{renderStars(avaliacao.ambienteNota, 12)}</div>
                </div>
                <div className="avaliacao-categoria">
                  <span className="cat-label">Comida</span>
                  <div className="cat-stars">{renderStars(avaliacao.comidaNota, 12)}</div>
                </div>
                <div className="avaliacao-categoria">
                  <span className="cat-label">Atendimento</span>
                  <div className="cat-stars">{renderStars(avaliacao.atendimentoNota, 12)}</div>
                </div>
                <div className="avaliacao-categoria">
                  <span className="cat-label">Preço</span>
                  <div className="cat-stars">{renderStars(avaliacao.precoNota, 12)}</div>
                </div>
              </div>

              {/* Comentários */}
              <div className="avaliacao-comentarios">
                {avaliacao.comidaComentario && (
                  <div className="comentario-item">
                    <span className="comentario-categoria">🍽️ Comida</span>
                    <p className="comentario-texto">{avaliacao.comidaComentario}</p>
                  </div>
                )}
                {avaliacao.ambienteComentario && (
                  <div className="comentario-item">
                    <span className="comentario-categoria">🏠 Ambiente</span>
                    <p className="comentario-texto">{avaliacao.ambienteComentario}</p>
                  </div>
                )}
                {avaliacao.atendimentoComentario && (
                  <div className="comentario-item">
                    <span className="comentario-categoria">👨‍🍳 Atendimento</span>
                    <p className="comentario-texto">{avaliacao.atendimentoComentario}</p>
                  </div>
                )}
                {avaliacao.precoComentario && (
                  <div className="comentario-item">
                    <span className="comentario-categoria">💰 Preço</span>
                    <p className="comentario-texto">{avaliacao.precoComentario}</p>
                  </div>
                )}
                {!avaliacao.comidaComentario && !avaliacao.ambienteComentario && 
                 !avaliacao.atendimentoComentario && !avaliacao.precoComentario && (
                  <p className="sem-comentario">Sem comentários</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      </>)}

      {/* Paginação */}
      {listaDisponivel && totalPaginas > 1 && (
        <div className="paginacao">
          <button 
            className="paginacao-button" 
            onClick={paginaAnterior} 
            disabled={paginaAtual === 0}
          >
            Anterior
          </button>
          
          <div className="paginacao-numeros">
            {Array.from({ length: totalPaginas }, (_, i) => (
              <button
                key={i}
                className={`paginacao-numero ${paginaAtual === i ? 'ativo' : ''}`}
                onClick={() => irParaPagina(i)}
              >
                {i + 1}
              </button>
            ))}
          </div>
          
          <button 
            className="paginacao-button" 
            onClick={proximaPagina} 
            disabled={paginaAtual === totalPaginas - 1}
          >
            Próximo
          </button>
        </div>
      )}
    </div>
  );
};

export default ListarAvaliacoes;